import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SessionStorageService } from '../session-storage.service';
import { AdminFunctionsService } from '../admin-functions.service';
import { mergeMap, tap, of, Subscription } from 'rxjs';
import { LocalStorageKey } from '../models/storageKey';
import { VenueDataMapper } from '../models/venueDataMapping';
import { PLAN_CATALOG } from '../models/planDetails';
import { AnalyticsService } from '../analytics/analytics.service';
import { FunnelTrackingService } from '../analytics/funnel-tracking.service';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { SignupStateService } from '../services/signup-state.service';

@Component({
  selector: 'app-review-venue',
  templateUrl: './review-venue.component.html',
  styleUrls: ['./review-venue.component.css']
})
export class ReviewVenueComponent implements OnInit, OnDestroy {
  email = '';
  contactEmail = '';
  contactPerson = '';
  contactPhoneNumber = '';
  businessName = '';
  venueType = '';
  venueTypeContinued = '';
  nonprofitStatus = '';
  selectedPlan = '';
  billingFrequency = '';
  streetAddress = '';
  city = '';
  stateProvince = '';
  postalCode = '';
  country = '';
  isLoading = false;
  private pageStartTime = 0;

  constructor(
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private adminFunctionsService: AdminFunctionsService,
    private analytics: AnalyticsService,
    private funnelTracking: FunnelTrackingService,
    private firebaseAuthService: FirebaseAuthService,
    private signupState: SignupStateService
  ) {}

  private storageSubscription?: Subscription;

  ngOnInit(): void {
    // Step views and page timing are handled by FunnelTrackingService;
    // pageStartTime only feeds intra-page timings below.
    this.pageStartTime = Date.now();

    // Reactive: the session doc restore is async, so re-render the summary
    // whenever the state lands/changes (e.g. hard reload directly on /review).
    this.storageSubscription = this.sessionStorageService.storage$.subscribe(storage => {
      const venueData = VenueDataMapper.mapFromLocalStorage(storage);

      this.email = venueData.userEmail || '';
      this.contactEmail = venueData.contactEmail || venueData.userEmail || '';
      this.contactPerson = venueData.contactPerson || '';
      this.contactPhoneNumber = venueData.contactPhoneNumber || '';
      this.businessName = venueData.venueName || '';
      this.venueType = venueData.venueCategoryType || '';
      this.venueTypeContinued = venueData.venueType || '';
      this.nonprofitStatus = storage[LocalStorageKey.NONPROFIT_STATUS] || '';
      this.selectedPlan = venueData.selectedPlan || '';
      this.billingFrequency = venueData.billingFrequency || 'monthly';
      this.streetAddress = venueData.streetAddress || '';
      this.city = venueData.city || '';
      this.stateProvince = venueData.stateProvince || '';
      this.postalCode = venueData.postalCode || '';
      this.country = venueData.country || '';
    });

    // Track review page view
    this.analytics.trackEvent('review_page_viewed', {
      venue_type: this.venueType,
      venue_subtype: this.venueTypeContinued,
      selected_plan: this.selectedPlan,
      billing_frequency: this.billingFrequency,
      from_page: 'plan_selection',
      has_contact_email: !!this.contactEmail,
      has_address: !!(this.streetAddress && this.city && this.stateProvince && this.postalCode),
      contact_email_same_as_login: this.contactEmail === this.email
    });
  }

  ngOnDestroy(): void {
    this.storageSubscription?.unsubscribe();
  }

  // ---- Display lines for the summary card ----

  get contactLine(): string {
    return [this.contactPerson, this.contactPhoneNumber].filter(Boolean).join(' · ')
      || 'Not provided';
  }

  get venueTypeLine(): string {
    const type = this.venueTypeContinued || 'Not selected';
    return this.nonprofitStatus === 'yes' ? `${type} · 501(c)(3)` : type;
  }

  get addressLine(): string {
    const countryName = this.country === 'US' ? 'United States'
      : this.country === 'CA' ? 'Canada'
      : this.country;
    const cityStateZip = [this.city, [this.stateProvince, this.postalCode].filter(Boolean).join(' ')]
      .filter(Boolean).join(', ');
    return [this.streetAddress, cityStateZip, countryName].filter(Boolean).join(', ')
      || 'Not provided';
  }

  get planLine(): string {
    const plan = PLAN_CATALOG.find(p => p.name === this.selectedPlan);
    if (!plan) {
      return 'No plan selected';
    }
    if (plan.name === 'starter') {
      return `${plan.displayName} · no monthly fee`;
    }
    return this.billingFrequency === 'annual'
      ? `${plan.displayName} · billed annually ($${plan.annualPrice}/yr)`
      : `${plan.displayName} · billed monthly ($${plan.monthlyPrice}/mo)`;
  }

  /** Venue-type phrase for the "Build your menu" sidebar item. */
  get menuTemplatePhrase(): string {
    switch (this.venueType) {
      case 'Concession Stand':
        return this.venueTypeContinued === 'Independent/Community'
          ? 'concession stands' : 'booster concessions';
      case 'Food Truck': return 'food trucks';
      case 'Golf Course': return 'golf service';
      case 'Stadium/Arena': return 'stadium concessions';
      default: return 'your venue type';
    }
  }

  submitVenue(): void {
    const storage = this.sessionStorageService.getAllStorage();
    const venueData = VenueDataMapper.mapFromLocalStorage(storage);

    const validation = VenueDataMapper.validateVenueData(venueData);
    if (!validation.isValid) {
      // Track validation error
      this.analytics.trackFormError('venue_submission', 'validation_failed', 'review',
        `Missing fields: ${validation.missingFields.join(', ')}`);
      alert(`Please complete all required fields: ${validation.missingFields.join(', ')}`);
      return;
    }

    const finalVenueData = {
      venueName: venueData.venueName,
      venueCategoryType: venueData.venueCategoryType,
      venueType: venueData.venueType,
      selectedPlan: venueData.selectedPlan,
      billingFrequency: venueData.billingFrequency,
      userEmail: venueData.userEmail,
      contactEmail: venueData.contactEmail,
      streetAddress: venueData.streetAddress,
      city: venueData.city,
      stateProvince: venueData.stateProvince,
      postalCode: venueData.postalCode,
      country: venueData.country,
      contactPerson: venueData.contactPerson,
      contactPhoneNumber: venueData.contactPhoneNumber,
      internetAvailability: venueData.internetAvailability
    };

    console.log('Venue data:', JSON.stringify(finalVenueData));
    this.isLoading = true;

    // Track submission attempt
    this.analytics.trackEvent('venue_submission_started', {
      venue_category_type: finalVenueData.venueCategoryType,
      venue_type: finalVenueData.venueType,
      selected_plan: finalVenueData.selectedPlan,
      billing_frequency: finalVenueData.billingFrequency,
      time_on_review_page: Date.now() - this.pageStartTime,
      total_signup_time: this.funnelTracking.getFunnelElapsedMs()
    });

    // Passwordless flow: submit the prospect; the backend creates the venue
    // and emails a portal invite. The anonymous session is discarded after.
    this.adminFunctionsService.submitSignupProspect(this.email, finalVenueData)
      .pipe(
        tap(response => {
          console.log('Submit Prospect Response:', response);

          // Track successful venue creation
          this.analytics.trackEvent('venue_created_successfully', {
            venue_category_type: finalVenueData.venueCategoryType,
            venue_type: finalVenueData.venueType,
            selected_plan: finalVenueData.selectedPlan,
            billing_frequency: finalVenueData.billingFrequency,
            api_response_time: Date.now() - this.pageStartTime
          });
        }),
        mergeMap(async () => {
          this.sessionStorageService.setItem(LocalStorageKey.REVIEW_VENUE, 'complete');

          // Stash display values for the confirmation page — the session doc
          // that holds them is deleted just below.
          this.sessionStorageService.setItem(LocalStorageKey.CONFIRMED_VENUE_NAME, finalVenueData.venueName);
          this.sessionStorageService.setItem(LocalStorageKey.CONFIRMED_EMAIL, finalVenueData.userEmail);

          // Clean up the anonymous session and its Firestore signup data
          // (clearSession also cancels any pending debounced writes so they
          // can't resurrect the doc after deletion)
          try {
            console.log('Cleaning up anonymous session...');
            await this.signupState.clearSession();
            await this.firebaseAuthService.signOutUser();
            console.log('Anonymous session cleanup complete');
          } catch (error) {
            console.error('Error cleaning up anonymous session:', error);
            // Continue anyway - the prospect was submitted successfully
          }

          return of(null);
        })
      )
      .subscribe(
        () => {
          this.isLoading = false;

          // The signup can no longer be abandoned — suppress the
          // pagehide abandonment event and record the step completion.
          this.funnelTracking.markFunnelCompleted();
          this.funnelTracking.trackStepCompleted('review', {
            venue_type: finalVenueData.venueCategoryType,
            selected_plan: finalVenueData.selectedPlan,
            billing_frequency: finalVenueData.billingFrequency
          });

          // Track final conversion - main success event
          this.analytics.trackSignupCompletion(finalVenueData, finalVenueData.selectedPlan,
            finalVenueData.billingFrequency, this.funnelTracking.getFunnelElapsedMs());

          // Set final user properties
          this.analytics.setUserProperties({
            signup_completed: true,
            completion_date: new Date().toISOString(),
            venue_category_type: finalVenueData.venueCategoryType,
            venue_type: finalVenueData.venueType,
            selected_plan: finalVenueData.selectedPlan,
            billing_frequency: finalVenueData.billingFrequency,
            signup_step: 'completed'
          });

          this.router.navigate(['/confirmation']);
        },
        error => {
          this.isLoading = false;
          console.error('Error creating venue:', error);

          // Track API error
          this.analytics.trackApiError('submitSignupProspect', error.code || 'unknown_error');

          // Track signup failure
          this.analytics.trackEvent('signup_failed', {
            venue_category_type: finalVenueData.venueCategoryType,
            venue_type: finalVenueData.venueType,
            selected_plan: finalVenueData.selectedPlan,
            billing_frequency: finalVenueData.billingFrequency,
            error_type: error.code || 'api_error',
            error_message: error.message || 'Unknown error',
            time_to_failure: this.funnelTracking.getFunnelElapsedMs()
          });

          alert('An error occurred while creating the venue. Please try again.');
        }
      );
  }

  navigateToEdit(section: string): void {
    // Track field edit navigation
    this.analytics.trackEvent('review_field_edit', {
      field_name: section,
      time_on_review_page: Date.now() - this.pageStartTime,
      venue_type: this.venueType,
      selected_plan: this.selectedPlan
    });

    switch (section) {
      case 'email':
      case 'businessName':
      case 'contact':
      case 'address':
        this.router.navigate(['/login']);
        break;
      case 'venueType':
        this.router.navigate(['/venueType']);
        break;
      case 'selectedPlan':
        this.router.navigate(['/choosePlan']);
        break;
      default:
        break;
    }
  }
}
